import {makeAutoObservable} from "mobx";
import {ValidatedValue} from "../../helpers/validatedValue";
import Validator from "../../helpers/validator";
import i18n from "../../i18n/i18n";
import {DamageModelWithDirection, TrailerConditionReportModel} from "../models/tcr";
import {OrderDetailModel, OrderStatusEnum} from "../models/order";
import {Direction} from "../models/transport";
import TcrUtils from "../../helpers/tcrUtils";
import {OrderFilesUtils} from "../../helpers/orderFilesUtils";
import {Utils} from "../../helpers/utils";

export interface ITcrStore {
    setOrderData: (order: OrderDetailModel) => void;

    orderStatus: OrderStatusEnum | null;
    setOrderStatus: (orderStatus: OrderStatusEnum | null) => void;

    orderId: string;
    setOrderId: (orderStatus: string) => void;

    direction: Direction | null;
    setDirection: (direction: Direction | null) => void;

    requestedTemp1: number | null;
    requestedTemp2: number | null;
    setRequestedTemps: (requestedTemp1: number | null, requestedTemp2: number | null) => void;

    clearDataForFile: () => void;

    trailerRegNo: string;
    setTrailerRegNo: (trailerRegNo: string) => void;

    transportCompany: string;
    setTransportCompany: (transportCompany: string) => void;

    portOfLoading: string;
    setPortOfLoading: (portOfLoading: string) => void;

    destinationPort: string;
    setDestinationPort: (destinationPort: string) => void;

    arrivalDepartureTime: string | Date | null;
    setArrivalDepartureTime: (arrivalDepartureTime: string | Date | null) => void;

    ferryName: string;
    setFerryName: (ferryName: string) => void;

    isDangerousGoods: boolean;
    setIsDangerousGoods: (isDangerousGoods: boolean) => void;

    isAddDamages: boolean;
    setIsAddDamages: (isAddDamages: boolean) => void;

    isEdit: boolean;
    setIsEdit: (isEdit: boolean) => void;

    entryWinch: ValidatedValue<boolean>;
    exitWinch: ValidatedValue<boolean>;
    entryPlumb: ValidatedValue<boolean>;
    exitPlumb: ValidatedValue<boolean>;
    entrySpareWheelsNo: ValidatedValue<number | null>;
    exitSpareWheelsNo: ValidatedValue<number | null>;
    entryWedgesNo: ValidatedValue<number | null>;
    exitWedgesNo: ValidatedValue<number | null>;
    entryActualTemp1: ValidatedValue<number | null>;
    exitActualTemp1: ValidatedValue<number | null>;
    entryActualTemp2: ValidatedValue<number | null>;
    exitActualTemp2: ValidatedValue<number | null>;

    damages: DamageModelWithDirection[];
    setDamages: (damages: DamageModelWithDirection[]) => void;
    selectedDamages: DamageModelWithDirection[];
    addSelectedDamage: (damage: DamageModelWithDirection) => void;
    removeSelectedDamage: (damage: DamageModelWithDirection) => void;
    copySelectedDamagesToDamages: () => void;
    copyDamagesToSelectedDamages: () => void;

    setTcr: (tcr: TrailerConditionReportModel | null | undefined) => void;
    isValid: boolean;
}

const positiveNumberOrEmptyValidators = [{
    name: "valueIsPositiveNumber",
    fn: Validator.isOnlyPositiveNumberWithCommaOrEmpty,
    errorMessage: () => i18n.t("errorMessages.onlyNumber")
}];

const isCorrectTemperatureValidators = [{
    name: "valueIsCorrectTemperature",
    fn: Validator.isCorrectNumberWithCommaOrEmpty,
    errorMessage: () => i18n.t("errorMessages.correctTemperature")
}];

export class TcrStore implements ITcrStore {
    _orderStatus: OrderStatusEnum | null;
    _orderId: string;
    _direction: Direction | null;
    _requestedTemp1: number | null;
    _requestedTemp2: number | null;
    _trailerRegNo: string;
    _transportCompany: string;
    _portOfLoading: string;
    _destinationPort: string;
    _arrivalDepartureTime: string | Date | null;
    _ferryName: string;
    _isDangerousGoods: boolean;
    _entryWinch: ValidatedValue<boolean>;
    _exitWinch: ValidatedValue<boolean>;
    _entryPlumb: ValidatedValue<boolean>;
    _exitPlumb: ValidatedValue<boolean>;
    _entrySpareWheelsNo: ValidatedValue<number | null>;
    _exitSpareWheelsNo: ValidatedValue<number | null>;
    _entryWedgesNo: ValidatedValue<number | null>;
    _exitWedgesNo: ValidatedValue<number | null>;
    _entryActualTemp1: ValidatedValue<number | null>;
    _entryActualTemp2: ValidatedValue<number | null>;
    _exitActualTemp1: ValidatedValue<number | null>;
    _exitActualTemp2: ValidatedValue<number | null>;
    _damages: DamageModelWithDirection[];
    _selectedDamages: DamageModelWithDirection[];
    _isAddDamages: boolean;
    _isEdit: boolean;

    constructor() {
        makeAutoObservable(this);

        this._orderStatus = null;
        this._orderId = "";
        this._direction = null;
        this._requestedTemp1 = null;
        this._requestedTemp2 = null;
        this._trailerRegNo = "";
        this._transportCompany = "";
        this._portOfLoading = "";
        this._destinationPort = "";
        this._arrivalDepartureTime = null;
        this._ferryName = "";
        this._isDangerousGoods = false;
        this._entryWinch = new ValidatedValue(false);
        this._exitWinch = new ValidatedValue(false);
        this._entryPlumb = new ValidatedValue(false);
        this._exitPlumb = new ValidatedValue(false);
        this._entrySpareWheelsNo = new ValidatedValue(null, positiveNumberOrEmptyValidators);
        this._exitSpareWheelsNo = new ValidatedValue(null, positiveNumberOrEmptyValidators);
        this._entryWedgesNo = new ValidatedValue(null, positiveNumberOrEmptyValidators);
        this._exitWedgesNo = new ValidatedValue(null, positiveNumberOrEmptyValidators);
        this._entryActualTemp1 = new ValidatedValue(null, isCorrectTemperatureValidators);
        this._exitActualTemp1 = new ValidatedValue(null, isCorrectTemperatureValidators);
        this._entryActualTemp2 = new ValidatedValue(null, isCorrectTemperatureValidators);
        this._exitActualTemp2 = new ValidatedValue(null, isCorrectTemperatureValidators);
        this._damages = [];
        this._selectedDamages = [];
        this._isAddDamages = false;
        this._isEdit = false;
    }

    setOrderData(order: OrderDetailModel | undefined) {
        if (!order) {
            return;
        }

        this.setOrderId(order.id || "");
        this.setOrderStatus(order.status || null);
        this.setRequestedTemps(order.requestedTemp1 || null, order.requestedTemp2 || null);
        this.setTrailerRegNo(order.trailerRegNo || "");
        this.setTransportCompany(order.transportCompany || "");
        this.setArrivalDepartureTime(order.arrivalDepartureTime || null);
        this.setFerryName(order.ferryName || "");

        if (OrderFilesUtils.isExportRelationType(order.relationType)) {
            this.setDestinationPort(order.originDestinationPort || "");
        }

        if (OrderFilesUtils.isImportRelationType(order.relationType)) {
            this.setPortOfLoading(order.originDestinationPort || "");
        }

        this.setIsDangerousGoods(order.isDangerousGoods);
    }

    get orderStatus() {
        return this._orderStatus;
    }

    setOrderStatus(orderStatus: OrderStatusEnum | null) {
        this._orderStatus = orderStatus;
    }

    get orderId() {
        return this._orderId;
    }

    setOrderId(orderId: string) {
        this._orderId = orderId;
    }

    get direction() {
        return this._direction;
    }

    setDirection(direction: Direction | null) {
        this._direction = direction;
    }

    get requestedTemp1() {
        return this._requestedTemp1;
    }

    get requestedTemp2() {
        return this._requestedTemp2;
    }

    setRequestedTemps(requestedTemp1: number | null, requestedTemp2: number | null) {
        this._requestedTemp1 = requestedTemp1;
        this._requestedTemp2 = requestedTemp2;
    }

    get trailerRegNo() {
        return this._trailerRegNo;
    }

    setTrailerRegNo(trailerRegNo: string) {
        this._trailerRegNo = trailerRegNo;
    }

    get transportCompany() {
        return this._transportCompany;
    }

    setTransportCompany(transportCompany: string) {
        this._transportCompany = transportCompany;
    }

    get portOfLoading() {
        return this._portOfLoading;
    }

    setPortOfLoading(portOfLoading: string) {
        this._portOfLoading = portOfLoading;
    }

    get destinationPort() {
        return this._destinationPort;
    }

    setDestinationPort(destinationPort: string) {
        this._destinationPort = destinationPort;
    }

    get arrivalDepartureTime() {
        return this._arrivalDepartureTime;
    }

    setArrivalDepartureTime(arrivalDepartureTime: string | Date | null) {
        this._arrivalDepartureTime = arrivalDepartureTime;
    }

    get ferryName() {
        return this._ferryName;
    }

    setFerryName(ferryName: string) {
        this._ferryName = ferryName;
    }

    get isDangerousGoods() {
        return this._isDangerousGoods;
    }

    setIsDangerousGoods(isDangerousGoods: boolean) {
        this._isDangerousGoods = isDangerousGoods;
    }

    get isAddDamages() {
        return this._isAddDamages;
    }

    setIsAddDamages(isAddDamages: boolean) {
        this._isAddDamages = isAddDamages;
    }

    get isEdit() {
        return this._isEdit;
    }

    setIsEdit(isEdit: boolean) {
        this._isEdit = isEdit;
    }

    get entryWinch() {
        return this._entryWinch;
    }

    get exitWinch() {
        return this._exitWinch;
    }

    get entryPlumb() {
        return this._entryPlumb;
    }

    get exitPlumb() {
        return this._exitPlumb;
    }

    get entrySpareWheelsNo() {
        return this._entrySpareWheelsNo;
    }

    get exitSpareWheelsNo() {
        return this._exitSpareWheelsNo;
    }

    get entryWedgesNo() {
        return this._entryWedgesNo;
    }

    get exitWedgesNo() {
        return this._exitWedgesNo;
    }

    get entryActualTemp1() {
        return this._entryActualTemp1;
    }

    get exitActualTemp1() {
        return this._exitActualTemp1;
    }

    get entryActualTemp2() {
        return this._entryActualTemp2;
    }

    get exitActualTemp2() {
        return this._exitActualTemp2;
    }

    get damages() {
        return this._damages;
    }

    get selectedDamages() {
        return this._selectedDamages;
    }

    setDamages(damages: DamageModelWithDirection[]) {
        this._damages = damages;
    }

    setSelectedDamages(damages: DamageModelWithDirection[]) {
        this._selectedDamages = damages;
    }

    setTcr(tcr: TrailerConditionReportModel | null | undefined) {
        this.clearValidation();

        this.entryWinch.setValue(tcr?.entryTrailerConditionReportData?.winch || false);
        this.exitWinch.setValue(tcr?.exitTrailerConditionReportData?.winch || false);
        this.entryPlumb.setValue(tcr?.entryTrailerConditionReportData?.plumb || false);
        this.exitPlumb.setValue(tcr?.exitTrailerConditionReportData?.plumb || false);
        this.entrySpareWheelsNo.setValue(Utils.convertToNumberOrNull(tcr?.entryTrailerConditionReportData?.spareWheelsNo));
        this.exitSpareWheelsNo.setValue(Utils.convertToNumberOrNull(tcr?.exitTrailerConditionReportData?.spareWheelsNo));
        this.entryWedgesNo.setValue(Utils.convertToNumberOrNull(tcr?.entryTrailerConditionReportData?.wedgesNo));
        this.exitWedgesNo.setValue(Utils.convertToNumberOrNull(tcr?.exitTrailerConditionReportData?.wedgesNo));
        this.entryActualTemp1.setValue(Utils.convertToNumberOrNull(tcr?.entryTrailerConditionReportData?.actualTemp1));
        this.exitActualTemp1.setValue(Utils.convertToNumberOrNull(tcr?.exitTrailerConditionReportData?.actualTemp1));
        this.entryActualTemp2.setValue(Utils.convertToNumberOrNull(tcr?.entryTrailerConditionReportData?.actualTemp2));
        this.exitActualTemp2.setValue(Utils.convertToNumberOrNull(tcr?.exitTrailerConditionReportData?.actualTemp2));

        const damages = [
            ...TcrUtils.setDirectionInDamages(Direction.IN, tcr?.entryTrailerConditionReportData?.damages) || [],
            ...TcrUtils.setDirectionInDamages(Direction.OUT, tcr?.exitTrailerConditionReportData?.damages) || []
        ];
        this.setDamages(damages);
        this.setSelectedDamages(damages);
        this.setDirection(TcrUtils.getDirection(tcr?.editableType || null));
    }

    clearValidation() {
        this.entryWinch.clearValidation();
        this.exitWinch.clearValidation();
        this.entryPlumb.clearValidation();
        this.exitPlumb.clearValidation();
        this.entrySpareWheelsNo.clearValidation();
        this.exitSpareWheelsNo.clearValidation();
        this.entryWedgesNo.clearValidation();
        this.exitWedgesNo.clearValidation();
        this.entryActualTemp1.clearValidation();
        this.exitActualTemp1.clearValidation();
        this.entryActualTemp2.clearValidation();
        this.exitActualTemp2.clearValidation();
    }

    get isValid() {
        this.entryWinch.validate();
        this.exitWinch.validate();
        this.entryPlumb.validate();
        this.exitPlumb.validate();
        this.entrySpareWheelsNo.validate();
        this.exitSpareWheelsNo.validate();
        this.entryWedgesNo.validate();
        this.exitWedgesNo.validate();
        this.entryActualTemp1.validate();
        this.exitActualTemp1.validate();
        this.entryActualTemp2.validate();
        this.exitActualTemp2.validate();

        return this.entryWinch.isValid &&
            this.exitWinch.isValid &&
            this.entryPlumb.isValid &&
            this.exitPlumb.isValid &&
            this.entrySpareWheelsNo.isValid &&
            this.exitSpareWheelsNo.isValid &&
            this.entryWedgesNo.isValid &&
            this.exitWedgesNo.isValid &&
            this.entryActualTemp1.isValid &&
            this.exitActualTemp1.isValid &&
            this.entryActualTemp2.isValid &&
            this.exitActualTemp2.isValid;
    }

    addSelectedDamage(damage: DamageModelWithDirection) {
        this._selectedDamages.push(damage);
    }

    removeSelectedDamage(damage: DamageModelWithDirection) {
        const index = TcrUtils.damageIndex(this.damages, damage);
        if (index !== undefined) {
            this._selectedDamages.splice(index, 1);
        }
    }

    copySelectedDamagesToDamages() {
        this._damages = Array.from(this._selectedDamages);
    }

    copyDamagesToSelectedDamages() {
        this._selectedDamages = Array.from(this._damages);
    }

    clearDataForFile() {
        this._trailerRegNo = "";
        this._transportCompany = "";
        this._portOfLoading = "";
        this._destinationPort = "";
        this._arrivalDepartureTime = null;
        this._ferryName = "";
        this._isDangerousGoods = false;
    }
}
